import { axiosAnonymousInstance } from "configurations/axiosConfig";
import { UploadConfiguration } from "entities/UIModel";
import React, {
  createContext,
  ReactNode,
  useEffect,
  useMemo,
  useState,
} from "react";

interface FeatureConfiguration {
  enableSignatures: boolean;
  enableReportPay: boolean;
  enableHelpCenter: boolean;
  enableSpyMode: boolean;
  inMaintenanceMode: boolean;
  maintenanceMessage: string;
}

interface GeneralConfiguration {
  saDomain: string;
  userInputDebounceTimeMs: number;
  filesSearchTimeoutMs: number;
  filesSearchDefaultLookupDays: number;
  maxAssignCount: number,
  verifyPolicyUploadConfiguration?: UploadConfiguration;
  registerUploadConfiguration?: UploadConfiguration;
  mainDomain: string;
  adminDomain: string;
  pspDomain: string;
}

interface State {
  featureConfig: FeatureConfiguration;
  generalConfig: GeneralConfiguration;
}

type Props = {
  children?: ReactNode;
};

const initialContext: State = {
  featureConfig: {
    enableSignatures: true,
    enableReportPay: true,
    enableHelpCenter: false,
    enableSpyMode: false,
    inMaintenanceMode: false,
    maintenanceMessage: "",
  },
  generalConfig: {
    saDomain: "",
    userInputDebounceTimeMs: 750,
    filesSearchTimeoutMs: 90000,
    filesSearchDefaultLookupDays: 10,
    maxAssignCount: 5000, // default only
    verifyPolicyUploadConfiguration: undefined,
    registerUploadConfiguration: undefined,
    mainDomain: "",
    adminDomain: "",
    pspDomain: "",
  },
};

const getFeatureConfig = async () => {
  const { data } = await axiosAnonymousInstance.get("/configuration/features");
  const retval: FeatureConfiguration = {
    enableSignatures: false, //data.Signatures.Enabled,
    enableReportPay: false, //data.ReportPay.Enabled,
    enableHelpCenter: data.HelpCenterEnabled,
    enableSpyMode: data.SpyMode,
    inMaintenanceMode: data.InMaintenanceMode,
    maintenanceMessage: data.MaintenanceMessage,
  };
  return retval;
};

const getGeneralConfig = async () => {
  const { data } = await axiosAnonymousInstance.get("/configuration/generalConfig");
  const retval: GeneralConfiguration = {
    saDomain: data.SADomain,
    userInputDebounceTimeMs: Number(data.UserInputDebounceTimeMs),
    filesSearchTimeoutMs: Number(data.FilesSearchTimeoutMs),
    filesSearchDefaultLookupDays: Number(data.FilesSearchDefaultLookupDays),
    maxAssignCount: Number(data.MaxAssignCount),
    verifyPolicyUploadConfiguration: data.VerifyPolicyUploadConfiguration ? JSON.parse(data.VerifyPolicyUploadConfiguration) : undefined,
    registerUploadConfiguration: data.RegisterUploadConfiguration ? JSON.parse(data.RegisterUploadConfiguration) : undefined,
    mainDomain: data.MainDomain,
    adminDomain: data.AdminDomain,
    pspDomain: data.PSPDomain,    
  };
  return retval;
};

const ConfigContext = createContext<State>(initialContext);

const ConfigContextProvider = ({ children }: Props) => {
  const [state, setState] = useState<State>(initialContext);

  useEffect(() => {
    const fetchConfigs = async () => {
      const featureConfigData = await getFeatureConfig();
      const generalConfigData = await getGeneralConfig();

      setState({
        featureConfig: featureConfigData,
        generalConfig: generalConfigData,
      });
    };

    fetchConfigs();
  }, []);

  const contextValue = useMemo(() => state, [state]);

  return <ConfigContext.Provider value={contextValue}>{children}</ConfigContext.Provider>;
};

export const useConfigContext = () => {
  const context = React.useContext(ConfigContext);
  if (!context) {
    throw new Error("useConfigContext must be used within a <ConfigContextProvider>.");
  }
  return context;
};

export default ConfigContextProvider;
